import React, { useState } from 'react';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import './DownloadAllButton.css';

const DownloadAllButton = ({ widgets }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Download all available PNG files as a zip
  const downloadAllPNGs = async () => {
    const zip = new JSZip();
    
    const widgetsWithImages = widgets.filter(widget => widget.image);
    
    // Fetch each image and add to zip
    for (const widget of widgetsWithImages) {
      const response = await fetch(widget.image);
      const blob = await response.blob();
      const filename = `${widget.title.replace(/\s+/g, '_')}.png`;
      zip.file(filename, blob);
    }
    
    // Download zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, 'all_results.zip');
    
    setShowDropdown(false);
  };

  // Download all widget content as one PDF
  const downloadAllAsPDF = async () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
    });
    
    const widgetsWithImages = widgets.filter(widget => widget.image);
    
    for (let i = 0; i < widgetsWithImages.length; i++) {
      const widget = widgetsWithImages[i];
      
      // Get widget content element
      const widgetElement = document.getElementById(`widget-${widget.id}`);
      
      // Convert widget to canvas
      const canvas = await html2canvas(widgetElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Add new page for each widget
      if (i > 0) {
        pdf.addPage();
      }
      
      // Calculate dimensions to fit image within PDF page
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, (pdfHeight - 20) / imgHeight);
      
      // Center the image horizontally
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      // Add image to PDF
      pdf.addImage(
        imgData,
        'PNG',
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );
    }
    
    // Download the PDF
    pdf.save('all_results.pdf');
    
    setShowDropdown(false);
  };

  return (
    <div className="download-all-button-container">
      <button 
        className="download-all-button"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Download All"
      >
        <Download size={20} />
        <span className="download-all-text">Download All</span>
      </button>
      
      {/* Dropdown menu with download options */}
      {showDropdown && (
        <>
          {/* Overlay to close dropdown when clicking outside */}
          <div 
            className="download-all-overlay" 
            onClick={() => setShowDropdown(false)}
          />
          <div className="download-all-dropdown">
            <button 
              className="download-all-option"
              onClick={downloadAllPNGs}
            >
              Download All PNGs (ZIP)
            </button>
            <button 
              className="download-all-option"
              onClick={downloadAllAsPDF}
            >
              Download All as PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadAllButton;